import { generatePath, Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { TLocation } from "@/locations/types/location.ts";
import { MACHINES_URL } from "@/machines/routes.ts";
import { MapView, CoordinateSet } from "@/map";
import LocationCardItemStatus from "@/locations/components/common/location-card/location-card-item-status.tsx";
import { useMemo, useState } from "react";
import { formatRelativeTimeUTC } from "@/shared/utils/date/format-relative-time";
import { Button } from "@/ui/components/ui/button";
import KPICard from "@/shared/components/ui/kpi-card/kpi-card.tsx";
import { Settings, Info } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/ui/components/ui/select";
import { useDateTimeRangeStore } from "@/shared/stores/useDateTimeRangeStore.ts";
import { useAggregatedKpis } from "@/machines/hooks/use-aggregated-kpis.ts";

interface LocationCardProps {
  location: TLocation;
}

const LocationCard = ({ location }: LocationCardProps) => {
  const { t } = useTranslation("locations");
  const navigate = useNavigate();
  const [selectedMachineId, setSelectedMachineId] = useState<string>("");
  const { value } = useDateTimeRangeStore();

  const deviceMxIds = (location?.machines ?? []).map((m) => m.device_mxid).filter(Boolean);
  const { data: agg, isLoading: isKpiLoading } = useAggregatedKpis({
    deviceMxIds,
    start: value.fromDate,
    end: value.toDate,
  });

  const lastActivityText: string = (location.onlineCount > 0) ? t("card.lastActivityNow") : formatRelativeTimeUTC(location.last_activity);

  const coordinates: CoordinateSet[] = useMemo(() =>
    location?.machines?.filter((machine) => machine?.last_gps?.latitude && machine?.last_gps?.longitude)
      .map((machine) => {
        return {
          name: machine.name,
          color: machine.color || "#EF4444",
          path: [
            {
              lat: machine.last_gps.latitude,
              lng: machine.last_gps.longitude,
              alt: machine.last_gps.altitude
            }
          ],
          isActive: machine.status === "online"
        }
      }) ?? [], [location?.machines]
  );

  const hasCoordinates = coordinates.length > 0;
  const isLoadingCoordinates = false;


  const handleDetailsClick = () => {
    navigate(generatePath(MACHINES_URL.LOCATION_DETAIL, { id: location.id.toString() }));
  };

  const handleSettingsClick = () => {
    navigate(`/settings/location?id=${location.id}`);
  };

  const handleMachineSelect = (machineId: string) => {
    setSelectedMachineId(machineId);
    // Navigate to machine detail or perform other action
    navigate(generatePath(MACHINES_URL.MACHINE_DETAIL, { id: machineId }));
  };

  // Helper function to format KPI values
  const formatKpiValue = (value: number | undefined, format: 'percentage' | 'number' | 'time' = 'number'): string => {
    if (value === undefined || !Number.isFinite(value)) return "-";

    switch (format) {
      case 'percentage':
        return `${value.toFixed(1)}%`;
      case 'time': {
        // Convert seconds to minutes
        const minutes = Math.round(value / 60);
        return `${minutes}min`;
      }
      case 'number':
      default:
        return value.toFixed(1);
    }
  };

  // Calculate Trucks per Hour
  const calculateTrucksPerHour = (): string => {
    if (!agg?.aggregated?.truckEventCount) return "-";
    const timeRangeHours = (value.toDate.getTime() - value.fromDate.getTime()) / (1000 * 60 * 60);
    if (timeRangeHours <= 0) return "-";
    const trucksPerHour = agg.aggregated.truckEventCount / timeRangeHours;
    return trucksPerHour.toFixed(1);
  };

  return (
    <div className="transition-transform hover:-translate-y-0.5 flex flex-col gap-3 bg-sidebar py-3 px-4 rounded-xl">
      {/* Top Section - Two Columns */}
      <div className="grid grid-cols-2 gap-4">
        {/* Left Column - Basic Info */}
        <div className="flex flex-col gap-3 min-w-0">
          <Link
            to={generatePath(MACHINES_URL.LOCATION_DETAIL, { id: location.id.toString() })}
            className="flex flex-col gap-2"
          >
            <div className="flex flex-col text-sm">
              <span className="font-semibold">{location?.name}</span>
              <span className="text-gray-400">{location?.organizationName}</span>
            </div>
            <div className="flex flex-col gap-0.5">
              <LocationCardItemStatus type="active" count={location.onlineCount} label={t("card.activeMachines")} />
              <LocationCardItemStatus type="inactive" count={location.offlineCount} label={t("card.inactiveMachines")} />
              <LocationCardItemStatus type="idle" count={location.idleCount} label={t("card.idleMachines")} />
              <LocationCardItemStatus type="total" count={location.machinesCount} label={t("card.totalMachines")} />
            </div>
            <div>
              <span className="text-gray-400 text-xs">
                {t("card.lastActivity", { time: lastActivityText })}
              </span>
            </div>
            {/* Additional KPI Cards Below Stats */}
            <div className="grid grid-cols-2 gap-2">
              <KPICard label={t("card.kpis.openCriticalAlerts")} value="0" size="small" />
              <KPICard label={t("card.kpis.alertsReported")} value="0" size="small" />
              <KPICard label={t("card.kpis.totalAlerts")} value="0" size="small" />
              <KPICard label={t("card.kpis.alertsCompliance")} value="0%" size="small" />
            </div>
          </Link>
        </div>

        {/* Right Column - KPI Cards */}
        <div className="grid grid-cols-2 gap-2 min-w-0">
          <KPICard
            label={t("card.kpis.availability")}
            value={isKpiLoading ? "..." : formatKpiValue(agg?.aggregated?.uptime as unknown as number, 'percentage')}
            size="small"
          />
          <KPICard
            label={t("card.kpis.utilization")}
            value={isKpiLoading ? "..." : formatKpiValue(agg?.aggregated?.efficiency, 'percentage')}
            size="small"
          />
          <KPICard
            label={t("card.kpis.totalTrucksLoaded")}
            value={isKpiLoading ? "..." : formatKpiValue(agg?.aggregated?.truckEventCount, 'number')}
            size="small"
          />
          <KPICard
            label={t("card.kpis.trucksPerHour")}
            value={isKpiLoading ? "..." : calculateTrucksPerHour()}
            size="small"
          />
          <KPICard
            label={t("card.kpis.avgLoadingTime")}
            value={isKpiLoading ? "..." : formatKpiValue(agg?.aggregated?.avgDurationSeconds, 'time')}
            size="small"
          />
          <KPICard
            label={t("card.kpis.avgWaitingTime")}
            value={isKpiLoading ? "..." : formatKpiValue(agg?.aggregated?.avgTimeBetweenTrucks, 'time')}
            size="small"
          />
          <KPICard
            label={t("card.kpis.avgBucketsPerTruck")}
            value={isKpiLoading ? "..." : formatKpiValue(agg?.aggregated?.avgDumpsPerTruck, 'number')}
            size="small"
          />
          <KPICard label={t("card.kpis.boulderCount")} value="15" size="small" />
          <KPICard label={t("card.kpis.boulderImpact")} value={t("card.kpis.boulderImpactMedium")} size="small" />
        </div>
      </div>

      {/* Buttons Section */}
      <div className="grid grid-cols-3 gap-2">
        <Button
          variant="outline"
          onClick={handleDetailsClick}
        >
          <Info className="h-4 w-4 mr-2" />
          {t("card.details")}
        </Button>

        <Select value={selectedMachineId} onValueChange={handleMachineSelect}>
          <SelectTrigger>
            <SelectValue placeholder={t("card.selectMachine")} />
          </SelectTrigger>
          <SelectContent>
            {location?.machines?.map((machine) => (
              <SelectItem key={machine.id} value={machine.id.toString()}>
                {machine.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Button
          variant="outline"
          onClick={handleSettingsClick}
        >
          <Settings className="h-4 w-4 mr-2" />
          {t("card.settings")}
        </Button>
      </div>

      {/* Map Section - Bottom */}
      <div className="rounded-xl overflow-hidden">
        <MapView
          key={`location-card-map-${location.id}`}
          basicCoordinates={coordinates}
          initialHeight={400}
          options={{
            defaultZoom: 14,
            zoomControl: false,
          }}
          locationId={location?.id ? location?.id : undefined}
          isLoading={isLoadingCoordinates}
          isEmpty={!hasCoordinates}
          emptyStateTitle={t("card.map.emptyTitle")}
          emptyStateDescription={t("card.map.emptyDescription")}
          loadingText={t("card.map.loading")}
        />
      </div>
    </div>
  );
};


export default LocationCard;
